/**
 * Readable references used across the domain, contact, transfer and registrant change pages:
 * every record is shown by its name and links to its page, the UUID stays in the tooltip.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../components/Badge';
import { EntityRef } from '../../components/EntityRef';
import { formatMoney } from '../../lib/format';
import { isCode, useLabels } from '../../lib/labels';
import { TechnicalDetail } from '../../components/States';
import { eppResultWith } from '../../lib/eppResult';
import { humanize } from './status';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

export type VocabGroup =
  | 'role'
  | 'tldClass'
  | 'direction'
  | 'actor'
  | 'object'
  | 'workflow'
  | 'lock'
  | 'documentType'
  | 'purpose'
  | 'dossierType'
  | 'classification'
  | 'check'
  | 'method'
  | 'channel'
  | 'failure'
  | 'orderStatus'
  | 'operation'
  | 'hold'
  | 'refund'
  | 'entry'
  | 'invoice'
  | 'account'
  | 'posting'
  | 'transferState'
  | 'dnsState'
  | 'trStatus'
  | 'errorClass'
  | 'trafficClass'
  | 'eppStatus'
  | 'evidence'
  | 'evidenceField';

/** Translated label for a free-text code (`domains:vocab.<group>.<CODE>`), falling back to the code humanised. */
export function useVocab(): (group: VocabGroup, code: string | null | undefined) => string {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const tr = t as unknown as Lookup;
  return (group, code) => {
    if (!code) return '—';
    if (group === 'workflow') return L.workflowType(code);
    if (!isCode(code)) return code; // free text (a registry message, a reason): as written
    return tr(`vocab.${group}.${code}`, { defaultValue: humanize(code) });
  };
}

/** Expiry notice type: `BEFORE_P30D` -> "30 days before expiry". */
export function useNoticeLabel(): (type: string) => string {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  return (type) => {
    const m = /^(BEFORE|AFTER)_P(\d+)D$/.exec(type);
    if (m) return tr(m[1] === 'BEFORE' ? 'vocab.notice.before' : 'vocab.notice.after', { count: Number(m[2]) });
    return tr(`vocab.notice.${type}`, { defaultValue: humanize(type) });
  };
}

export const domainHref = (asciiName: string, tab?: string) => `/domains/${encodeURIComponent(asciiName)}${tab ? `/${tab}` : ''}`;
export const registryHref = (code: string) => `/registry/registries/${encodeURIComponent(code)}`;
export const contactHref = (registry: string, handle: string) => `/contacts/${encodeURIComponent(registry)}/${encodeURIComponent(handle)}`;

export function DomainRef({ ascii, unicode, id, tab, sub }: { ascii: string; unicode?: string | null; id?: string | null; tab?: string; sub?: ReactNode }) {
  const label = unicode ?? ascii;
  return <EntityRef label={label} to={domainHref(ascii, tab)} id={id} sub={sub ?? (label !== ascii ? <span className="font-mono">{ascii}</span> : undefined)} />;
}

export function CustomerRef({ id, name, sub }: { id: string | null | undefined; name: string | null | undefined; sub?: ReactNode }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return <EntityRef label={name} to={`/customers/${encodeURIComponent(id)}`} id={id} sub={sub} />;
}

export function RegistryRef({ code, name, sub }: { code: string | null | undefined; name?: string | null; sub?: ReactNode }) {
  if (!code) return <span className="text-muted-foreground">—</span>;
  return <EntityRef label={name ?? code} to={registryHref(code)} sub={sub ?? (name ? code : undefined)} />;
}

/**
 * A registrant subject. The name is null for staff who may not read subjects: the subject type
 * is shown instead, never the ID.
 */
export function SubjectRef({ id, name, type, sub }: { id: string | null | undefined; name: string | null | undefined; type?: string | null; sub?: ReactNode }) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  if (!id) return <span className="text-muted-foreground">{t('common.noSubject')}</span>;
  const typeLabel = type ? tr(`subject.types.${type.toUpperCase()}`, { defaultValue: humanize(type) }) : undefined;
  if (!name) {
    return (
      <span title={t('common.unnamedHint')}>
        <EntityRef label={<span className="italic">{t('common.unnamed')}</span>} to={`/subjects/${encodeURIComponent(id)}`} id={id} sub={sub ?? typeLabel} />
      </span>
    );
  }
  return <EntityRef label={name} to={`/subjects/${encodeURIComponent(id)}`} id={id} sub={sub ?? typeLabel} />;
}

export function WorkflowRef({ id, type, state, sub }: { id: string | null | undefined; type?: string | null; state?: string | null; sub?: ReactNode }) {
  const vocab = useVocab();
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  if (!id) return <span className="text-muted-foreground">—</span>;
  const stateLabel = state ? tr(`status.workflow.${state.toUpperCase()}`, { defaultValue: humanize(state) }) : undefined;
  return <EntityRef label={type ? vocab('workflow', type) : t('registry.workflow')} to={`/ops/workflows/${encodeURIComponent(id)}`} id={id} sub={sub ?? stateLabel} />;
}

/** A registry command, named by its EPP operation. */
export function TxRef({ id, operation, sub }: { id: string | null | undefined; operation: string | null | undefined; sub?: ReactNode }) {
  const L = useLabels();
  if (!id) return <span className="text-muted-foreground">—</span>;
  return <EntityRef label={<span title={operation ?? undefined}>{L.eppCommand(operation)}</span>} to={`/registry/transactions/${encodeURIComponent(id)}`} id={id} sub={sub} />;
}

export function ContactRef({ registry, handle, sub }: { registry: string; handle: string | null | undefined; sub?: ReactNode }) {
  const { t } = useTranslation('domains');
  if (!handle) return <span className="text-muted-foreground">{t('contacts.noHandle')}</span>;
  return <EntityRef label={<span className="font-mono">{handle}</span>} to={contactHref(registry, handle)} sub={sub} />;
}

/**
 * Who did something: staff and the domain's own customer by name, system components by what they
 * do (never the service name), other actors by their type.
 */
export function ActorName({ type, name }: { type: string | null | undefined; name: string | null | undefined }) {
  const vocab = useVocab();
  const L = useLabels();
  if (!type) return <span className="text-muted-foreground">—</span>;
  if (!name) return <span>{vocab('actor', type)}</span>;
  if (type === 'SYSTEM' || type === 'EXTERNAL') return <span>{L.source(name)}</span>;
  return (
    <span>
      <span className="font-medium">{name}</span> <span className="text-xs text-muted-foreground">· {vocab('actor', type)}</span>
    </span>
  );
}

export function RoleBadge({ role }: { role: string }) {
  const vocab = useVocab();
  return (
    <Badge tone={role === 'REGISTRANT' ? 'info' : 'neutral'} icon={false} title={role}>
      {vocab('role', role)}
    </Badge>
  );
}

export function Money({ amount, currency }: { amount: string | null | undefined; currency: string | null | undefined }) {
  if (amount === null || amount === undefined || !currency) return <span className="text-muted-foreground">—</span>;
  return <span className="tabular whitespace-nowrap">{formatMoney({ amount, currency })}</span>;
}

/** Monospace code chip for technical tokens (EPP commands, clTRIDs, statuses from the registry). */
export const codeClass = 'rounded bg-muted px-1.5 py-0.5 font-mono text-xs';

/** A registry command's result: a sentence by its EPP result code, the code beside it, the registry's own text folded away (lib/eppResult.ts). */
export function EppResult({ code, message }: { code: number; message: string | null }) {
  const { t, i18n } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const r = eppResultWith(code, message, (key) => (i18n.exists(key) ? tr(key) : undefined));
  return (
    <div className="grid gap-0.5 text-xs">
      <span>
        {r.text} <code className={codeClass}>{code}</code>
      </span>
      <TechnicalDetail detail={r.detail} />
    </div>
  );
}
